import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useDismissOnOutside } from '../../shell/use-dismiss-on-outside';
import { HelpNote } from '../../help';
import './TaskColumnsControl.css';

// A stable ref so the dismiss hook's dependency array does not change
// identity every render while the panel is closed.
const NOT_OPEN = { current: null };

export interface ColumnChoice {
  id: string;
  label: string;
  visible: boolean;
  hideable: boolean;
}

/**
 * The Tasks toolbar's "Columns" control: tick a column to show it, move
 * it up or down, or go back to the default.
 *
 * Up/down buttons rather than drag-and-drop. They work the same with a
 * finger on an iPad, a mouse at the front desk and a keyboard, and they
 * need no drag library and no long-press to discover. The list is short
 * enough (nine) that a few presses is never a chore.
 *
 * The panel is the same anchored-popover shape as the top bar's
 * Favourites, with the same shared dismiss hook (Escape, or a press
 * outside it). Everything inside is a native checkbox or button, so
 * Tab, Space and Enter already do what they should.
 */
export function TaskColumnsControl({
  columns,
  customised,
  onToggle,
  onMove,
  onReset,
}: {
  /** In the order they show in the table. */
  columns: readonly ColumnChoice[];
  /** False while the layout is the default: Reset would do nothing. */
  customised: boolean;
  onToggle: (id: string, visible: boolean) => void;
  onMove: (id: string, delta: -1 | 1) => void;
  onReset: () => void;
}) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelId = useId();

  useDismissOnOutside(
    open ? panelRef : NOT_OPEN,
    useCallback(() => setOpen(false), []),
    buttonRef,
  );

  // Into the panel on open, so a keyboard user lands on the list they
  // asked for rather than having to find it.
  useEffect(() => {
    if (open) panelRef.current?.querySelector<HTMLElement>('input, button')?.focus();
  }, [open]);

  function onPanelKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Escape') return;
    // The shared hook closes on Escape too; this adds the one thing it
    // cannot know to do, which is hand focus back to the trigger.
    setOpen(false);
    buttonRef.current?.focus();
  }

  function move(id: string, delta: -1 | 1, atEdge: boolean) {
    if (!atEdge) onMove(id, delta);
  }

  return (
    <div className="nx-task-columns">
      <button
        ref={buttonRef}
        type="button"
        className="nx-task-columns__btn"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? panelId : undefined}
        data-testid="tasks-columns-btn"
      >
        Columns
      </button>
      {open && (
        <div
          ref={panelRef}
          id={panelId}
          className="nx-task-columns__panel"
          role="dialog"
          aria-label="Columns"
          onKeyDown={onPanelKeyDown}
          data-testid="tasks-columns-panel"
        >
          <ul className="nx-task-columns__list">
            {columns.map((c, i) => {
              const first = i === 0;
              const last = i === columns.length - 1;
              return (
                // Keyed by column, never by position: React then moves
                // the item (and puts focus back on the moved button after
                // the commit), so repeated presses keep moving the same
                // column. Keyed by index, focus would stay put while the
                // columns slid underneath it.
                <li key={c.id} className="nx-task-columns__item">
                  <label className="nx-task-columns__label">
                    <input
                      type="checkbox"
                      checked={c.visible}
                      disabled={!c.hideable}
                      onChange={(e) => onToggle(c.id, e.target.checked)}
                      data-testid={`tasks-column-toggle-${c.id}`}
                    />
                    <span>{c.label}</span>
                    {!c.hideable && <span className="nx-task-columns__fixed">Always shown</span>}
                  </label>
                  {/* aria-disabled rather than disabled at either end: a
                      disabled button drops focus, and the end is exactly
                      where a keyboard user's last press lands. */}
                  <span className="nx-task-columns__moves">
                    <button
                      type="button"
                      className="nx-task-columns__move"
                      aria-label={`Move ${c.label} up`}
                      title="Move up"
                      aria-disabled={first}
                      onClick={() => move(c.id, -1, first)}
                      data-testid={`tasks-column-up-${c.id}`}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="nx-task-columns__move"
                      aria-label={`Move ${c.label} down`}
                      title="Move down"
                      aria-disabled={last}
                      onClick={() => move(c.id, 1, last)}
                      data-testid={`tasks-column-down-${c.id}`}
                    >
                      ↓
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>
          <HelpNote id="tasks.columns" />
          <div className="nx-task-columns__foot">
            <button
              type="button"
              className="nx-task-columns__reset"
              aria-disabled={!customised}
              onClick={() => {
                if (customised) onReset();
              }}
              data-testid="tasks-columns-reset"
            >
              Reset to default
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
